// contexts/TicketContext.tsx - GÜNCELLENMİŞ
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Ticket, Company, User, ModuleType } from '../types';
import { ticketsAPI, companiesAPI, usersAPI, reportsAPI } from '../services/api';
import { useAuth } from './AuthContext';

interface ReportData {
  monthlyData: any[];
  statusData: any[];
  moduleData: any[];
  companyData: any[];
  priorityData: any[];
  generalStats: {
    totalTickets: number;
    resolutionRate: number;
    avgResolutionTime: number;
    activeTickets: number;
  };
}

interface TicketContextType {
  tickets: Ticket[];
  companies: Company[];
  users: User[];
  loading: boolean;
  reportData: ReportData;
  reportLoading: boolean;
  
  // State'ler
  selectedCompany: Company | null;
  selectedModule: ModuleType | null;
  setSelectedCompany: (company: Company | null) => void;
  setSelectedModule: (module: ModuleType | null) => void;
  
  // Methods
  getTickets: () => Promise<void>;
  getTicket: (id: number) => Promise<Ticket | null>;
  addTicket: (ticketData: any) => Promise<void>;
  updateTicket: (id: number, updates: Partial<Ticket>) => Promise<void>;
  deleteTicket: (id: number) => Promise<void>;
  getCompanies: () => Promise<void>;
  getUsers: () => Promise<void>;
  getReportData: () => Promise<void>;
}

const TicketContext = createContext<TicketContextType | undefined>(undefined);

export const TicketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  
  // State'ler
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedModule, setSelectedModule] = useState<ModuleType | null>(null);

  // Rapor verileri
  const [reportData, setReportData] = useState<ReportData>({
    monthlyData: [],
    statusData: [],
    moduleData: [],
    companyData: [],
    priorityData: [],
    generalStats: {
      totalTickets: 0,
      resolutionRate: 0,
      avgResolutionTime: 0,
      activeTickets: 0
    }
  });

  const { isAuthenticated } = useAuth();

  // SADECE authenticated olduğunda verileri çek
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔄 TicketContext: User authenticated, loading initial data...');
      loadInitialData();
    } else {
      console.log('🔐 TicketContext: User not authenticated, skipping data load');
      // Verileri temizle
      setTickets([]);
      setCompanies([]);
      setUsers([]);
    }
  }, [isAuthenticated]);

  const loadInitialData = async () => {
    if (!isAuthenticated) {
      console.log('🚫 Skipping data load - user not authenticated');
      return;
    }

    try {
      setLoading(true);
      console.log('📥 Loading companies, users, and tickets...');
      
      // Temel verileri yükle
      await Promise.all([getCompanies(), getUsers()]);
      
      console.log('✅ Initial data loaded successfully');
    } catch (error) {
      console.error('❌ Initial data loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTickets = async () => {
    if (!isAuthenticated) {
      console.log('🚫 Cannot get tickets - user not authenticated');
      throw new Error('User not authenticated');
    }

    try {
      setLoading(true);
      const response = await ticketsAPI.getTickets();
      setTickets(response.data);
      console.log('✅ Tickets loaded:', response.data.length);
    } catch (error) {
      console.error('❌ Get tickets error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getTicket = async (id: number): Promise<Ticket | null> => {
    if (!isAuthenticated) {
      console.log('🚫 Cannot get ticket - user not authenticated');
      return null;
    }

    try {
      const response = await ticketsAPI.getTicket(id);
      return response.data;
    } catch (error) {
      console.error('❌ Get ticket error:', error);
      return null;
    }
  };

const addTicket = async (ticketData: any) => {
  if (!isAuthenticated) {
    console.log('🚫 Cannot add ticket - user not authenticated');
    throw new Error('User not authenticated');
  }

  try {
    setLoading(true);
    const response = await ticketsAPI.createTicket(ticketData);
    
    // Yeni ticket'ı listeye ekle
    setTickets(prev => [response.data, ...prev]);
    
    // ✅ TALEP EKLENDİKTEN SONRA RAPOR YENİLE
    console.log('✅ Ticket created, refreshing reports...');
    await getReportData();
    
    console.log('✅ Ticket created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Add ticket error:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

// ✅ updateTicket - GÜNCELLEME SONRASI RAPOR YENİLE
const updateTicket = async (id: number, updates: Partial<Ticket>) => {
  if (!isAuthenticated) {
    console.log('🚫 Cannot update ticket - user not authenticated');
    throw new Error('User not authenticated');
  }

  try {
    setLoading(true);
    const response = await ticketsAPI.updateTicket(id, updates);
    
    // Ticket'ı güncelle
    setTickets(prev => prev.map(ticket => 
      ticket.id === id ? { ...ticket, ...response.data } : ticket
    ));
    
    // ✅ GÜNCELLEME SONRASI RAPOR YENİLE
    console.log('✅ Ticket updated, refreshing reports...');
    await getReportData();
    
    console.log('✅ Ticket updated successfully:', id);
    return response.data;
  } catch (error) {
    console.error('❌ Update ticket error:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

// ✅ deleteTicket - SİLME SONRASI RAPOR YENİLE
const deleteTicket = async (id: number) => {
  if (!isAuthenticated) {
    console.log('🚫 Cannot delete ticket - user not authenticated');
    throw new Error('User not authenticated');
  }

  try {
    setLoading(true);
    await ticketsAPI.deleteTicket(id);
    
    // Ticket'ı listeden kaldır
    setTickets(prev => prev.filter(ticket => ticket.id !== id));
    
    // ✅ SİLME SONRASI RAPOR YENİLE
    console.log('✅ Ticket deleted, refreshing reports...');
    await getReportData();
    
    console.log('✅ Ticket deleted successfully:', id);
  } catch (error) {
    console.error('❌ Delete ticket error:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  const getCompanies = async () => {
    if (!isAuthenticated) {
      console.log('🚫 Cannot get companies - user not authenticated');
      return;
    }

    try {
      const response = await companiesAPI.getCompanies();
      setCompanies(response.data);
      console.log('✅ Companies loaded:', response.data.length);
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.warn('⚠️ Rate limit reached for companies API');
        return;
      }
      console.error('❌ Get companies error:', error);
      throw error;
    }
  };

  const getUsers = async () => {
    if (!isAuthenticated) {
      console.log('🚫 Cannot get users - user not authenticated');
      return;
    }

    try {
      const response = await usersAPI.getSupportUsers();
      setUsers(response.data);
      console.log('✅ Users loaded:', response.data.length);
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.warn('⚠️ Rate limit reached for users API');
        return;
      }
      console.error('❌ Get users error:', error);
      throw error;
    }
  };

const getReportData = async () => {
  if (!isAuthenticated) {
    console.log('🚫 Cannot get reports - user not authenticated');
    return;
  }

  // ✅ ÖNBELLEK KONTROLÜ
  if (reportData.monthlyData.length > 0 && !reportLoading) {
    console.log('📊 Report data already loaded, skipping...');
    return;
  }

  try {
    setReportLoading(true);
    console.log('📊 Loading report data from API...');
    
    // ✅ 6 API - 6 RESPONSE
    const [
      monthlyRes, 
      statusRes, 
      moduleRes, 
      companyRes,    // ✅ 4. INDEX
      priorityRes,   // ✅ 5. INDEX  
      statsRes       // ✅ 6. INDEX
    ] = await Promise.all([
      reportsAPI.getMonthlyReports(),
      reportsAPI.getStatusDistribution(),
      reportsAPI.getModuleDistribution(),
      reportsAPI.getCompanyDistribution(),  // ✅ 4.
      reportsAPI.getPriorityDistribution(), // ✅ 5.
      reportsAPI.getGeneralStats()          // ✅ 6.
    ]);

    setReportData({
      monthlyData: monthlyRes.data || [],
      statusData: statusRes.data || [],
      moduleData: moduleRes.data || [],
      companyData: companyRes.data || [],    // ✅ DOĞRU!
      priorityData: priorityRes.data || [],
      generalStats: statsRes.data || {
        totalTickets: 0,
        resolutionRate: 0,
        avgResolutionTime: 0,
        activeTickets: 0
      }
    });

    console.log('✅ Report data loaded:', {
      monthly: monthlyRes.data?.length,
      status: statusRes.data?.length,
      module: moduleRes.data?.length,
      company: companyRes.data?.length,      // ✅ DOĞRU!
      priority: priorityRes.data?.length,
      stats: statsRes.data
    });
  } catch (error) {
    console.error('❌ Get report data error:', error);
    setReportData({
      monthlyData: [],
      statusData: [],
      moduleData: [],
      companyData: [],
      priorityData: [],
      generalStats: { totalTickets: 0, resolutionRate: 0, avgResolutionTime: 0, activeTickets: 0 }
    });
  } finally {
    setReportLoading(false);
  }
};
  return (
    <TicketContext.Provider value={{
      tickets,
      companies,
      users,
      loading,
      reportData,
      reportLoading,
      
      // State'ler
      selectedCompany,
      selectedModule,
      setSelectedCompany,
      setSelectedModule,
      
      // Methods
      getTickets,
      getTicket,
      addTicket,
      updateTicket,
      deleteTicket,
      getCompanies,
      getUsers,
      getReportData,
    }}>
      {children}
    </TicketContext.Provider>
  );
};

export const useTicket = () => {
  const context = useContext(TicketContext);
  if (context === undefined) {
    throw new Error('useTicket must be used within a TicketProvider');
  }
  return context;
};